import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthService } from '../services/authService';
import { AuthState, User, LoginRequest, AuthRequest, PasswordlessAuthRequest, PasswordlessLoginRequest, OTPVerificationRequest } from '../types/auth';
import toast from 'react-hot-toast';

interface AuthContextType extends AuthState {
  login: (data: LoginRequest) => Promise<void>;
  register: (data: AuthRequest) => Promise<void>;
  passwordlessLogin: (data: PasswordlessLoginRequest) => Promise<{ success: boolean; session?: string }>;
  passwordlessRegister: (data: PasswordlessAuthRequest) => Promise<{ success: boolean; session?: string }>;
  verifyOTP: (data: OTPVerificationRequest) => Promise<void>;
  resendOTP: (data: PasswordlessLoginRequest) => Promise<void>;
  forgotPassword: (data: { email?: string; phone?: string }) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: true, 
        isLoading: false,
        error: null 
      };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    default:
      return state;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const token = AuthService.getStoredToken();
      if (!token) {
        dispatch({ type: 'LOGOUT' });
        return;
      }

      if (!AuthService.isTokenValid(token)) {
        await AuthService.logout();
        dispatch({ type: 'LOGOUT' });
        return;
      }

      const response = await AuthService.getUserProfile();
      if (response.success && response.data?.user) {
        dispatch({ type: 'SET_USER', payload: response.data.user });
        dispatch({ type: 'SET_TOKEN', payload: token });
      } else {
        await AuthService.logout();
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      await AuthService.logout();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const login = async (data: LoginRequest) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.login(data);
      
      if (response.success && response.data?.user && response.data?.token) {
        dispatch({ type: 'SET_USER', payload: response.data.user });
        dispatch({ type: 'SET_TOKEN', payload: response.data.token });
        toast.success('Login successful!');
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Login failed' });
        toast.error(response.error || 'Login failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const register = async (data: AuthRequest) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.register(data);
      
      if (response.success) {
        if (response.message && response.message.includes('confirmation code')) {
          window.location.href = `/confirm-email?email=${encodeURIComponent(data.email)}`;
        } else {
          toast.success('Registration successful! Please wait for admin approval.');
        }
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Registration failed' });
        toast.error(response.error || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const passwordlessLogin = async (data: PasswordlessLoginRequest): Promise<{ success: boolean; session?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.passwordlessLogin(data);
      
      if (response.success) {
        toast.success('Código de verificación enviado');
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true, session: response.data?.session };
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Login failed' });
        toast.error(response.error || 'Login failed');
        return { success: false };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false };
    }
  };

  const passwordlessRegister = async (data: PasswordlessAuthRequest): Promise<{ success: boolean; session?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.passwordlessRegister(data);
      
      if (response.success) {
        toast.success('Código de verificación enviado');
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true, session: response.data?.session };
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Registration failed' });
        toast.error(response.error || 'Registration failed');
        return { success: false };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false };
    }
  };

  const verifyOTP = async (data: OTPVerificationRequest) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.verifyOTP(data);
      
      if (response.success && response.data?.user && response.data?.token) {
        dispatch({ type: 'SET_USER', payload: response.data.user });
        dispatch({ type: 'SET_TOKEN', payload: response.data.token });
        toast.success('¡Inicio de sesión exitoso!');
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'OTP verification failed' });
        toast.error(response.error || 'OTP verification failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OTP verification failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const resendOTP = async (data: PasswordlessLoginRequest) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.resendOTP(data);
      
      if (response.success) {
        toast.success('Código de verificación reenviado');
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to resend OTP' });
        toast.error(response.error || 'Failed to resend OTP');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend OTP';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  };

  const forgotPassword = async (data: { email?: string; phone?: string }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await AuthService.forgotPassword(data);
      
      if (response.success) {
        toast.success('Password reset email sent');
        return { success: true };
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Password reset failed' });
        toast.error(response.error || 'Password reset failed');
        return { success: false };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      return { success: false };
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    passwordlessLogin,
    passwordlessRegister,
    verifyOTP,
    resendOTP,
    forgotPassword,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 